import {
    allStepTypesWorkflow,
    helpRequestWorkflowDraft,
    helpRequestWorkflowPublished,
    reEngagementWorkflow,
    segmentEntryWorkflow,
    welcomeSeriesWorkflow,
} from './fixtures/seedWorkflows';
import type { Workflow, DrainingVersion } from '~/types/workflow.types';
import type { WorkflowComment } from './workflowsClient.types';

const STORAGE_KEY = 'voxie:workflow-builder:workflows:v1';

// Version of the storage format itself (not of individual workflows). Bump it when the
// store shape changes; readers reseed on mismatch.
export const STORAGE_SCHEMA_VERSION = 25;

export interface WorkflowsStore {
    schema_version: number;
    // Entries ordered ascending by version.
    workflows: Record<string, Workflow[]>;
    // Mock runtime state: older published versions still draining active instances.
    drainingVersions: Record<string, DrainingVersion[]>;
    // Free-floating canvas comments (team-scoped, not attached to any workflow).
    comments: WorkflowComment[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const buildSeedStore = (): WorkflowsStore => {
    return {
        schema_version: STORAGE_SCHEMA_VERSION,
        workflows: {
            [welcomeSeriesWorkflow.workflow_id]: [{ ...structuredClone(welcomeSeriesWorkflow), x: 0, y: 0 }],
            [helpRequestWorkflowPublished.workflow_id]: [
                { ...structuredClone(helpRequestWorkflowPublished), x: 704, y: 0 },
                { ...structuredClone(helpRequestWorkflowDraft), x: 704, y: 0 },
            ],
            [reEngagementWorkflow.workflow_id]: [{ ...structuredClone(reEngagementWorkflow), x: 1408, y: 0 }],
            // Intentionally unpositioned to exercise auto placement.
            [segmentEntryWorkflow.workflow_id]: [structuredClone(segmentEntryWorkflow)],
            [allStepTypesWorkflow.workflow_id]: [{ ...structuredClone(allStepTypesWorkflow), x: 2816, y: 0 }],
        },
        drainingVersions: {
            [helpRequestWorkflowPublished.workflow_id]: [{ version: 1, active_count: 42 }],
        },
        comments: [],
    };
};

const parseStore = (json: string): WorkflowsStore | null => {
    let parsed: unknown;
    try {
        parsed = JSON.parse(json);
    } catch {
        return null;
    }

    if (
        !isRecord(parsed) ||
        parsed.schema_version !== STORAGE_SCHEMA_VERSION ||
        !isRecord(parsed.workflows) ||
        !isRecord(parsed.drainingVersions) ||
        !Array.isArray(parsed.comments)
    ) {
        return null;
    }

    // The store only ever holds data we wrote, so its entries are trusted as-is rather than
    // re-validated field by field.
    for (const versions of Object.values(parsed.workflows)) {
        if (!Array.isArray(versions)) {
            return null;
        }
    }

    return {
        schema_version: STORAGE_SCHEMA_VERSION,
        workflows: parsed.workflows as Record<string, Workflow[]>,
        drainingVersions: parsed.drainingVersions as Record<string, DrainingVersion[]>,
        comments: parsed.comments as WorkflowComment[],
    };
};

const seedStore = (): WorkflowsStore => {
    const seeded = buildSeedStore();
    try {
        writeStore(seeded);
    } catch {
        // Booting must stay fail-soft even when persistence is broken; the seeded
        // in-memory store still lets the app render.
    }

    return seeded;
};

// Fail-soft: a missing, corrupted, or outdated store is replaced by the seed fixture so
// the app always boots with valid data — this is a dev/demo store, nothing to preserve.
export const readStore = (): WorkflowsStore => {
    const json = localStorage.getItem(STORAGE_KEY);
    if (json === null) {
        return seedStore();
    }

    const store = parseStore(json);
    if (store === null) {
        console.error('Workflow builder storage was corrupted or outdated; reseeding.');
        return seedStore();
    }

    return store;
};

// Throws on failure so callers can surface it; the client maps this to an axios-style
// 500 so a failed write never silently loses the mutation on the next read.
export const writeStore = (store: WorkflowsStore): void => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
};
